import type { FC } from 'react';
import { useEffect, useState } from 'react';
import { PapiGallection } from '@wix/da-papi-types';
import { useNormalizedDeviationStream } from '@wix/da-shared-react/pkg/utils/hooks/useStream';
import StudioHeaderMobile from '../../../StudioHeaderMobile';
import { useDraftsSelect } from '../../../../hooks/useDraftsSelect';
import { STUDIO_DEVIATIONS_STREAM } from '../../../../../constants';
import StashTabsMobile, { TabType } from '../../../stash/StashTabsMobile';
import LayoutContainer from '../../_partials/LayoutContainer';
import DeviationsTab from '../../_partials/DeviationsTab';
import FoldersTab from '../../_partials/FoldersTab';
import StudioBlankSlate from '../../../StudioBlankSlate';

export interface Props {
  subfolders?: PapiGallection[];
}

const StashLayout: FC<Props> = ({ subfolders = [] }) => {
  const { streamId, streamConfig, streamThrottle } = STUDIO_DEVIATIONS_STREAM;
  const { items: deviations, isFetching } = useNormalizedDeviationStream(
    streamId,
    streamThrottle,
    { ...streamConfig }
  );

  const {
    selected,
    selectedCount,
    allIsSelected,
    hasSelection,
    onSelect,
    onSelectAll,
  } = useDraftsSelect(deviations);

  const hasDeviations = deviations.length > 0;
  const [hasSubfolders, setHasSubfolders] = useState<boolean>(
    subfolders.length > 0
  );
  const [activeTab, setActiveTab] = useState(
    !hasDeviations && hasSubfolders ? TabType.Folders : TabType.Files
  );

  useEffect(() => {
    if (!hasDeviations && hasSubfolders) {
      setActiveTab(TabType.Folders);
    } else if (!hasSubfolders) {
      // If we come to render and a first subfolder has just
      // been created, switch to Folders tab
      if (subfolders.length > 0 && activeTab === TabType.Files) {
        setHasSubfolders(true);
        setActiveTab(TabType.Folders);
      } else {
        setActiveTab(TabType.Files);
      }
    }
  }, [subfolders, hasDeviations, hasSubfolders, activeTab]);

  const tabs =
    hasDeviations && hasSubfolders ? (
      <StashTabsMobile
        activeTab={activeTab}
        onChange={tab => setActiveTab(tab)}
      />
    ) : null;

  // tabs are: 1) meant to be 'not fixed' so we need to put them in the scrollable area
  // and 2) not shown shown when a selection is active
  // that's unless there's no results and we're showing the blank slate
  const areTabsInListsScrollableArea = !hasSelection;

  return (
    <LayoutContainer>
      <StudioHeaderMobile
        selectedCount={selectedCount}
        allIsSelected={allIsSelected}
        onSelectAll={onSelectAll}
      />

      {activeTab === TabType.Files && !hasDeviations ? (
        <>
          {tabs}
          <StudioBlankSlate />
        </>
      ) : null}
      {activeTab === TabType.Files && hasDeviations ? (
        <DeviationsTab
          elementBeforeList={areTabsInListsScrollableArea && tabs}
          deviations={deviations}
          isFetching={isFetching}
          selected={selected}
          hasSelection={hasSelection}
          onSelect={onSelect}
        />
      ) : null}

      {activeTab === TabType.Folders && (
        <FoldersTab elementBeforeList={areTabsInListsScrollableArea && tabs} />
      )}
    </LayoutContainer>
  );
};
StashLayout.displayName = 'StashLayout';

export default StashLayout;
